import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@lattice/frontend/lib/utils/ui";

const tooltipVariants = cva(
  "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  {
    variants: {
      position: {
        top: "data-[side=top]:slide-in-from-bottom-2",
        bottom: "data-[side=bottom]:slide-in-from-top-2",
        left: "data-[side=left]:slide-in-from-right-2",
        right: "data-[side=right]:slide-in-from-left-2",
      },
      size: {
        default: "max-w-xs",
        sm: "max-w-[200px]",
        md: "max-w-[300px]",
        lg: "max-w-[400px]",
      },
    },
    defaultVariants: {
      position: "top",
      size: "default",
    },
  },
);

interface T_TooltipProps extends VariantProps<typeof tooltipVariants> {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  delayDuration?: number;
  skipDelayDuration?: number;
  open?: boolean;
  position?: "top" | "bottom" | "left" | "right" | undefined;
  size?: "default" | "sm" | "md" | "lg";
}

const Tooltip = ({
  content,
  children,
  position,
  size,
  className,
  open,
  delayDuration = 300,
  skipDelayDuration = 100,
  ...props
}: T_TooltipProps) => {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
    >
      <TooltipPrimitive.Root open={open}>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={position}
            className={cn(tooltipVariants({ position, size, className }))}
            sideOffset={4}
            {...props}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-primary" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

export { Tooltip };
